import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { chatAPI } from '../services/api';
import { useAlertContext } from '../context/AlertContext';
import ChatRoom from '../components/chat/ChatRoom';
import Alert from '../components/common/Alert';

const ChatPage = () => {
	const [sessionId, setSessionId] = useState('');
	const [name, setName] = useState('');
	const [email, setEmail] = useState('');
	const [summary, setSummary] = useState(false);
	const [transcript, setTranscript] = useState(false);
	const [privacyConsent, setPrivacyConsent] = useState(false);
	const [showChatRoom, setShowChatRoom] = useState(false);
	const [showDetailsForm, setShowDetailsForm] = useState(false);
	const [privacyPolicyMessage, setPrivacyPolicyMessage] = useState('');
	const [privacyPolicyLink, setPrivacyPolicyLink] = useState('');
	const [formError, setFormError] = useState('');
	const [startTimeUTC, setStartTimeUTC] = useState(null);
	const [formattedStartTime, setFormattedStartTime] = useState('');

	const navigate = useNavigate();
	const location = useLocation();
	const { showAlert } = useAlertContext();
	const baseUrl = process.env.REACT_APP_BASE_URL || '/';

	useEffect(() => {
		// Parse query parameters
		const params = new URLSearchParams(location.search);
		const sessionIdParam = params.get('session_id');
		const nameParam = params.get('name');
		const emailParam = params.get('email');
		const summaryParam = params.get('summary') === 'on';
		const transcriptParam = params.get('transcript') === 'on';

		// If we have a session ID in the URL
		if (sessionIdParam) {
			setSessionId(sessionIdParam);

			// If we also have details, show the chat room directly
			if (nameParam) {
				setName(nameParam);
				setEmail(emailParam || '');
				setSummary(summaryParam);
				setTranscript(transcriptParam);

				// Store in session storage for later use
				sessionStorage.setItem('chat_name', nameParam);
				sessionStorage.setItem('chat_email', emailParam || '');
				sessionStorage.setItem('chat_summary', summaryParam);
				sessionStorage.setItem('chat_transcript', transcriptParam);

				setShowChatRoom(true);
			} else {
				// Show the details form for this session
				fetchSessionDetails(sessionIdParam);
			}
		}
	}, [location.search]);

	const fetchSessionDetails = async (id) => {
		try {
			const response = await chatAPI.joinSession(id);

			if (response.success && response.form_data) {
				// If access is granted, it means the user has already provided details.
				// We can now redirect to the chat room with the details in the URL.
				const params = new URLSearchParams({
					session_id: id,
					name: response.form_data.name,
					email: response.form_data.email || '',
					summary: response.form_data.summary ? 'on' : '',
					transcript: response.form_data.transcript ? 'on' : '',
				});
				navigate(`${baseUrl}chat?${params.toString()}`);
			} else if (response.requires_details) {
				// The user needs to provide details.
				setPrivacyPolicyMessage(response.privacy_policy_message || '');
				setPrivacyPolicyLink(response.privacy_policy_link || '');
				// Pre-fill form if data is available
				if (response.form_data) {
					setName(response.form_data.name || '');
					setEmail(response.form_data.email || '');
					setSummary(response.form_data.summary || false);
					setTranscript(response.form_data.transcript || false);
				}
				setShowDetailsForm(true);
			} else {
				// A terminal error occurred (e.g., dialogue expired).
				setFormError(
					response.error_message ||
						response.error ||
						'An unknown error occurred.'
				);
				setShowDetailsForm(false);
				if (response.start_time_utc) {
					setStartTimeUTC(response.start_time_utc);
					formatStartTime(response.start_time_utc);
				}
			}
		} catch (error) {
			console.error('Error joining session:', error);
			setFormError('Error connecting to server. Please try again.');
		}
	};

	const formatStartTime = (utcTimestamp) => {
		try {
			const date = new Date(utcTimestamp);

			const options = {
				year: 'numeric',
				month: 'short',
				day: '2-digit',
				hour: '2-digit',
				minute: '2-digit',
				timeZoneName: 'short',
			};

			const formatted = date.toLocaleString(undefined, options);
			setFormattedStartTime(formatted);
		} catch (error) {
			console.error('Error formatting date:', error);
		}
	};

	const handleSessionIdSubmit = (e) => {
		e.preventDefault();
		if (!sessionId.trim()) return;

		navigate(`${baseUrl}chat?session_id=${encodeURIComponent(sessionId)}`);
	};

	const handleDetailsSubmit = async (e) => {
		e.preventDefault();

		if (!name.trim()) {
			showAlert('Name is required', 'danger');
			return;
		}

		if (!privacyConsent) {
			showAlert('You must agree to the privacy policy', 'danger');
			return;
		}

		try {
			const details = {
				name,
				email,
				summary: summary ? 'on' : '',
				transcript: transcript ? 'on' : '',
				privacy_policy_consent: 'on',
			};

			const response = await chatAPI.submitDetails(sessionId, details);

			if (response.success) {
				// Store in session storage for later use
				sessionStorage.setItem('chat_name', name);
				sessionStorage.setItem('chat_email', email || '');
				sessionStorage.setItem('chat_summary', summary);
				sessionStorage.setItem('chat_transcript', transcript);

				// Redirect to chat room with all params
				const params = new URLSearchParams({
					session_id: sessionId,
					name,
					email: email || '',
					summary: summary ? 'on' : '',
					transcript: transcript ? 'on' : '',
				});

				navigate(`${baseUrl}chat?${params.toString()}`);
			} else {
				showAlert(
					response.error_message || 'An unknown error occurred.',
					'danger'
				);
			}
		} catch (error) {
			console.error('Error submitting details:', error);
			showAlert('Error connecting to server. Please try again.', 'danger');
		}
	};

	return (
		<div>
			<Alert />

			{!showDetailsForm && !showChatRoom && (
				<div className="container">
					<div className="row justify-content-center align-items-center min-vh-100">
						<div className="col-md-4">
							<div className="card">
								<div className="card-body">
									<form id="form" onSubmit={handleSessionIdSubmit}>
										<div id="alert-container">
											{formError && (
												<div className="alert alert-danger" role="alert">
													{formError}{' '}
													{formattedStartTime && (
														<span id="formatted-date">
															{formattedStartTime}
														</span>
													)}
												</div>
											)}
										</div>
										<div className="form-group">
											<label htmlFor="session_id">Participant ID</label>
											<input
												type="text"
												className="form-control"
												id="session_id"
												name="session_id"
												value={sessionId}
												onChange={(e) => setSessionId(e.target.value)}
												required
											/>
										</div>
										<button type="submit" className="btn btn-primary btn-block">
											Join
										</button>
									</form>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}

			{showDetailsForm && !showChatRoom && (
				<div className="container mt-5 col-md-4">
					<div className="card">
						<div className="card-body">
							<div id="alert-container"></div>
							<form id="form-details" onSubmit={handleDetailsSubmit}>
								<input
									type="hidden"
									id="session_id"
									name="session_id"
									value={sessionId}
								/>
								<div className="form-group">
									<label htmlFor="name">Name (required)</label>
									<input
										type="text"
										className="form-control"
										id="name"
										name="name"
										placeholder="Enter your name"
										value={name}
										onChange={(e) => setName(e.target.value)}
										required
									/>
								</div>
								<div className="form-group">
									<label htmlFor="email">Email (optional)</label>
									<input
										type="email"
										className="form-control"
										id="email"
										name="email"
										placeholder="Enter your email"
										value={email}
										onChange={(e) => setEmail(e.target.value)}
									/>
								</div>
								<div className="form-group">
									<label>Output Type</label>
									<div className="form-check">
										<input
											className="form-check-input"
											type="checkbox"
											id="summary"
											name="summary"
											checked={summary}
											onChange={(e) => setSummary(e.target.checked)}
										/>
										<label className="form-check-label" htmlFor="summary">
											Summary
										</label>
									</div>
									<div className="form-check">
										<input
											className="form-check-input"
											type="checkbox"
											id="transcript"
											name="transcript"
											checked={transcript}
											onChange={(e) => setTranscript(e.target.checked)}
										/>
										<label className="form-check-label" htmlFor="transcript">
											Transcript
										</label>
									</div>
								</div>
								<div className="form-group">
									<label>Privacy Policy</label>
									<div
										className="border p-2"
										style={{ height: '150px', overflowY: 'scroll' }}
									>
										<p>{privacyPolicyMessage}</p>
										{privacyPolicyLink && (
											<a
												href={privacyPolicyLink}
												target="_blank"
												rel="noopener noreferrer"
											>
												{privacyPolicyLink}
											</a>
										)}
									</div>
									<div className="form-check mt-2">
										<input
											className="form-check-input"
											type="checkbox"
											id="privacyPolicy"
											name="privacy_policy_consent"
											checked={privacyConsent}
											onChange={(e) => setPrivacyConsent(e.target.checked)}
											required
										/>
										<label className="form-check-label" htmlFor="privacyPolicy">
											I have read and agree to the privacy policy
										</label>
									</div>
								</div>
								<button type="submit" className="btn btn-primary btn-block">
									Enter Dialogue Instance
								</button>
							</form>
						</div>
					</div>
				</div>
			)}

			{showChatRoom && (
				<ChatRoom
					sessionId={sessionId}
					name={name}
					onLeave={() => {
						setShowChatRoom(false);
						setShowDetailsForm(false);
						setSessionId('');
						setName('');
						setEmail('');
						setSummary(false);
						setTranscript(false);
						setPrivacyConsent(false);
						setFormError('');
						setStartTimeUTC(null);
						setFormattedStartTime('');
						setPrivacyPolicyMessage('');
						setPrivacyPolicyLink('');
						sessionStorage.removeItem('session_id');
						sessionStorage.removeItem('chat_username');
						sessionStorage.removeItem('chat_name');
						sessionStorage.removeItem('chat_email');
						sessionStorage.removeItem('chat_summary');
						sessionStorage.removeItem('chat_transcript');
						navigate(`${baseUrl}chat`);
					}}
				/>
			)}
		</div>
	);
};

export default ChatPage;
